import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  CREATE_SESSION_VALUE,
  RENAME_SESSION_VALUE,
  useForecastSessionPicker,
} from 'src/features/forecastSessions/useForecastSessionPicker';
import { cn } from 'src/lib/utils';

type ForecastSessionPickerProps = {
  className?: string;
};

export function ForecastSessionPicker({
  className,
}: ForecastSessionPickerProps): ReactElement {
  const { t } = useTranslation('forecast');
  const {
    sessions,
    activeSession,
    isBusy,
    isCreating,
    isRenaming,
    isSelectOpen,
    isCreateDialogOpen,
    isRenameDialogOpen,
    createSessionName,
    renameSessionName,
    setCreateSessionName,
    setRenameSessionName,
    setIsCreateDialogOpen,
    setIsRenameDialogOpen,
    closeCreateDialog,
    closeRenameDialog,
    handleSelectOpenChange,
    handleSessionChange,
    handleCreateSession,
    handleRenameSession,
  } = useForecastSessionPicker();

  return (
    <>
      <div className={cn('flex items-center gap-3', className)}>
        <Select
          disabled={isBusy}
          onOpenChange={handleSelectOpenChange}
          onValueChange={handleSessionChange}
          open={isSelectOpen}
          value={activeSession?.id ?? undefined}
        >
          <SelectTrigger
            className='w-[17rem]'
            disabled={isBusy}
            id='forecast-session-select'
          >
            <SelectValue placeholder={t('Select a session')} />
          </SelectTrigger>
          <SelectContent>
            {sessions.map((session) => (
              <SelectItem key={session.id} value={session.id}>
                {session.name}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value={CREATE_SESSION_VALUE}>
              {t('Create new session')}
            </SelectItem>
            <SelectItem
              disabled={activeSession === undefined}
              value={RENAME_SESSION_VALUE}
            >
              {t('Rename current session')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            closeCreateDialog();
          }
        }}
        open={isCreateDialogOpen}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('Create new session')}</DialogTitle>
          </DialogHeader>
          <div className='grid gap-2'>
            <span className='font-medium text-sm'>{t('Session name')}</span>
            <Input
              autoFocus
              id='forecast-session-name'
              maxLength={255}
              onChange={(event) => setCreateSessionName(event.target.value)}
              placeholder={t('Session name')}
              value={createSessionName}
            />
          </div>
          <DialogFooter className='gap-2'>
            <Button
              disabled={isCreating}
              onClick={closeCreateDialog}
              variant='outline'
            >
              {t('Cancel')}
            </Button>
            <Button
              disabled={isCreating || !createSessionName.trim()}
              onClick={handleCreateSession}
            >
              {t('Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        onOpenChange={(open) => {
          setIsRenameDialogOpen(open);
          if (!open) {
            closeRenameDialog();
          }
        }}
        open={isRenameDialogOpen}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('Rename session')}</DialogTitle>
          </DialogHeader>
          <div className='grid gap-2'>
            <span className='font-medium text-sm'>{t('Session name')}</span>
            <Input
              autoFocus
              id='forecast-session-rename'
              maxLength={255}
              onChange={(event) => setRenameSessionName(event.target.value)}
              placeholder={t('Session name')}
              value={renameSessionName}
            />
          </div>
          <DialogFooter className='gap-2'>
            <Button
              disabled={isRenaming}
              onClick={closeRenameDialog}
              variant='outline'
            >
              {t('Cancel')}
            </Button>
            <Button
              disabled={isRenaming || !renameSessionName.trim()}
              onClick={handleRenameSession}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
